import { useMemo } from "react";
import { useApiData } from "./useApiData";
import { unwrapArray } from "../utils/api";

/**
 * Revenue + receipt count + average receipt for a period - all from ONE call
 * (hourly_sales.php, kasabill/kasabillpayment), instead of two (revenue used to
 * be fetched separately from document/documentitem via summary_items.php).
 * Note: this means the KPI cards on the dashboard use kasabill as their
 * source, while the "Promet artikala" page still uses document - the numbers
 * normally match, but technically they come from two different records.
 */
export function usePeriodStats(startDate, endDate, warehouse, refreshKey, enabled = true) {
  const whParam = warehouse ? `&warehouse=${warehouse}` : "";
  const hasRange = Boolean(startDate && endDate) && enabled;

  const { data: hourlySalesRaw, loading: loadingHourly, error: errorHourly } = useApiData(
    hasRange ? `/hourly_sales.php?od=${startDate}&do=${endDate}${whParam}` : null,
    refreshKey
  );

  return useMemo(() => {
    const rows = unwrapArray(hourlySalesRaw);
    const revenue = rows.reduce((sum, s) => sum + (parseFloat(s.vrednost) || 0), 0);
    const receiptCount = rows.reduce((sum, s) => sum + (parseFloat(s.ukupno_racuna) || 0), 0);
    const averageReceipt = receiptCount > 0 ? revenue / receiptCount : 0;

    return {
      promet: revenue,
      brojRacuna: receiptCount,
      prosecanRacun: averageReceipt,
      loading: loadingHourly,
      error: errorHourly,
      hourlyRows: rows, // for RevenueChart - avoids it fetching the same data again itself
    };
  }, [hourlySalesRaw, loadingHourly, errorHourly]);
}

/** Percent change: ((current - previous) / previous) * 100. */
export function percentChange(current, previous) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}
